/**
 * Maps flat API player models from `getSquadPlayersByPosition` into the
 * lightweight {@link SquadPlayer} shape used by squad planning components.
 * @module features/squad-planning/utils/formatPlayerRow
 */

import type {
  AcademyPositionPlayerModel,
  SquadPositionPlayerModel,
  TargetPositionPlayerModel,
} from "@/generated/graphql";

import type { SquadPlayer, TierName } from "../types";

/** Shared fields present on all three player model types. */
type BasePositionPlayer = Pick<
  SquadPositionPlayerModel,
  | "id"
  | "name"
  | "firstName"
  | "knownName"
  | "lastName"
  | "age"
  | "gpr"
  | "gpv"
  | "gpvMin"
  | "gpvMax"
  | "minutes"
  | "wage"
  | "amortisationAnnual"
  | "netBookValue"
  | "estVal"
  | "contractExpiry"
  | "status"
  | "position"
  | "positions"
  | "nationalityName"
  | "isManual"
  | "teamName"
  | "transitionType"
>;

/**
 * Converts shared base fields into a {@link SquadPlayer}.
 * @param player - The flat API player model
 * @param tierName - The tier to assign
 * @param isAcademy - Whether the player belongs to the academy
 * @returns A normalised SquadPlayer
 */
function mapBase(
  player: BasePositionPlayer,
  tierName: TierName,
  isAcademy: boolean
): SquadPlayer {
  return {
    id: player.id,
    displayName:
      (player.firstName != null && player.firstName !== "") ||
      (player.lastName != null && player.lastName !== "")
        ? (player?.name ?? "")
        : player.name,
    age: player.age ?? null,
    gprScore: player.gpr ?? null,
    wage: player.wage ?? null,
    estValuation: player.estVal ?? null,
    readinessTag: null,
    isAcademy,
    tierName,
    positionAbbreviations: player.positions?.length
      ? player.positions
      : player.position
        ? [player.position]
        : [],
    teamName: player.teamName ?? null,
    nationalityName: player.nationalityName ?? null,
    primaryPosition: player.position ?? null,
    minutesPlayed: player.minutes ?? null,
    gpv: player.gpv ?? null,
    gpvMin: player.gpvMin ?? null,
    gpvMax: player.gpvMax ?? null,
    contractExpires: player.contractExpiry ?? null,
    isInjured: player.status === "INJURED",
    status: player.status ?? null,
    fairFee: null,
    expectedFee: null,
    watchlistCount: 0,
    transitionType: player.transitionType ?? null,
    // SE-7414: the query now selects both, so the KEY is always present and
    // `null` here means "the payload carried this field and this player has
    // no figure" — which is what `payloadCarries` in `useSquadPlanningTable`
    // distinguishes from `undefined` ("the field was never selected").
    amortisationAnnual: player.amortisationAnnual ?? null,
    netBookValue: player.netBookValue ?? null,
  };
}

/**
 * Maps a first-team player from the API into a {@link SquadPlayer}.
 * @param player - The first-team player model from `getSquadPlayersByPosition`
 * @returns A SquadPlayer with tierName "First Team"
 */
export function formatFirstTeamPlayer(
  player: SquadPositionPlayerModel
): SquadPlayer {
  return mapBase(player, "First Team", false);
}

/**
 * Maps an academy player from the API into a {@link SquadPlayer}.
 * @param player - The academy player model from `getSquadPlayersByPosition`
 * @returns A SquadPlayer with tierName "Academy" and readinessTag
 */
export function formatAcademyPlayer(
  player: AcademyPositionPlayerModel
): SquadPlayer {
  return {
    ...mapBase(player, "Academy", true),
    readinessTag: player.readinessTag ?? null,
  };
}

/**
 * Maps a target player from the API into a {@link SquadPlayer}.
 * @param player - The target player model from `getSquadPlayersByPosition`
 * @returns A SquadPlayer with tierName "Targets" and watchlistCount
 */
export function formatTargetPlayer(
  player: TargetPositionPlayerModel
): SquadPlayer {
  return {
    ...mapBase(player, "Targets", false),
    watchlistCount: player.sourceWatchlists?.length ?? 0,
  };
}
